import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Topbar from './Topbar';
import { toast } from 'react-toastify';
import GraphComponent from './GraphComponent';
import { urls } from './config.dev';

// ─── Mobile Detection Hook ────────────────────────────────────────────────────
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
      || window.innerWidth <= 768;
  });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
};

function SalesDetails() {
  const { employee } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [currentTime, setCurrentTime] = useState(new Date());
  const [salesData, setSalesData] = useState([]);
  const [expensesData, setExpensesData] = useState([]);
  const [totalDrugsUsed, setTotalDrugsUsed] = useState(0);
  const [totalDrugsBought, setTotalDrugsBought] = useState(0);
  const [totalPatients, setTotalPatients] = useState(0);
  const [shiftType, setShiftType] = useState('Day');
  const [selectedSalesCategory, setSelectedSalesCategory] = useState('All');
  const [selectedExpensesCategory, setSelectedExpensesCategory] = useState('All');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedEndDate, setSelectedEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [selectedMonthOnly, setSelectedMonthOnly] = useState(String(new Date().getMonth() + 1).padStart(2, '0'));
  const [selectedYearForMonth, setSelectedYearForMonth] = useState(String(new Date().getFullYear()));
  const [selectedYear, setSelectedYear] = useState(String(new Date().getFullYear()));
  const [dateFilterType, setDateFilterType] = useState('single');
  const [isLoading, setIsLoading] = useState(false);
  const [clinicName, setClinicName] = useState('');
  const [urlToken, setUrlToken] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isTokenVerified, setIsTokenVerified] = useState(false);
  const [viewType, setViewType] = useState('detailed');
  const [showGraphs, setShowGraphs] = useState(false);
  const [hoveredNavItem, setHoveredNavItem] = useState(null);
  const [employeeName, setEmployeeName] = useState('');
  const [currentTheme, setCurrentTheme] = useState('blue');

  // Mobile-specific state
  const [mobileTab, setMobileTab] = useState('summary'); // 'summary' | 'sales' | 'expenses' | 'filters'
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [mobileShiftTab, setMobileShiftTab] = useState('Day');

  // ─── Set white browser chrome on mobile ─────────────────────────────────────
  useEffect(() => {
    if (isMobile) {
      let metaThemeColor = document.querySelector('meta[name="theme-color"]');
      if (!metaThemeColor) {
        metaThemeColor = document.createElement('meta');
        metaThemeColor.setAttribute('name', 'theme-color');
        document.head.appendChild(metaThemeColor);
      }
      metaThemeColor.setAttribute('content', '#ffffff');

      let metaStatusBar = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
      if (!metaStatusBar) {
        metaStatusBar = document.createElement('meta');
        metaStatusBar.setAttribute('name', 'apple-mobile-web-app-status-bar-style');
        document.head.appendChild(metaStatusBar);
      }
      metaStatusBar.setAttribute('content', 'black-translucent');
    }
  }, [isMobile]);

  // Force white theme on mobile
  const effectiveTheme = isMobile ? 'white' : currentTheme;

  // ─── DESIGN TOKENS ───────────────────────────────────────────────────────────
  const colors = {
    blue: {
      sidebarBg: '#0a1e4a',
      sidebarBorder: '#1e3a8a',
      activeNavBg: '#2563eb',
      activeNavText: '#ffffff',
      inactiveNavText: '#e0e7ff',
      navHoverBg: '#1e3a8a',
      sectionHeaderText: '#94a3b8',
      mainBg: '#f0f4ff',
      cardBg: '#ffffff',
      cardBorder: '#d4e0ff',
      accent: '#16a34a',
      accentLight: '#dcfce7',
      danger: '#dc2626',
      dangerLight: '#fef2f2',
      warning: '#d97706',
      warningLight: '#fffbeb',
      info: '#2563eb',
      infoLight: '#e0edff',
      skyBlue: '#38bdf8',
      skyBlueLight: '#e0f2fe',
      textPrimary: '#0a1e4a',
      textSecondary: '#1e3a8a',
      textMuted: '#4b5563',
      tableHeader: '#e8f0fe',
      tableBorder: '#cbd5e1',
      tableRowHover: '#f0f7ff',
      badgeGreen: { bg: '#dcfce7', text: '#166534' },
      badgeRed: { bg: '#fee2e2', text: '#991b1b' },
      badgeOrange: { bg: '#ffedd5', text: '#9a3412' },
      badgeBlue: { bg: '#dbeafe', text: '#1e40af' },
      badgeSky: { bg: '#e0f2fe', text: '#0369a1' },
      badgeGray: { bg: '#f1f5f9', text: '#475569' },
      filterSection: '#0d2257',
      collapseButtonBg: '#1e3a8a',
      collapseButtonHover: '#2563eb',
      collapseButtonText: '#ffffff',
      tooltipBg: '#1e293b',
      tooltipText: '#ffffff',
      iconBright: '#fbbf24',
      iconHover: '#f59e0b',
      sidebarText: '#ffffff',
      sidebarTextMuted: '#a5b4fc',
      pureWhite: '#ffffff',
    },
    white: {
      sidebarBg: '#ffffff',
      sidebarBorder: '#e2e8f0',
      activeNavBg: '#f1f5f9',
      activeNavText: '#0f172a',
      inactiveNavText: '#475569',
      navHoverBg: '#f8fafc',
      sectionHeaderText: '#64748b',
      mainBg: '#f8fafc',
      cardBg: '#ffffff',
      cardBorder: '#e2e8f0',
      accent: '#16a34a',
      accentLight: '#dcfce7',
      danger: '#dc2626',
      dangerLight: '#fef2f2',
      warning: '#d97706',
      warningLight: '#fffbeb',
      info: '#2563eb',
      infoLight: '#eff6ff',
      skyBlue: '#38bdf8',
      skyBlueLight: '#e0f2fe',
      textPrimary: '#0f172a',
      textSecondary: '#475569',
      textMuted: '#64748b',
      tableHeader: '#f1f5f9',
      tableBorder: '#e2e8f0',
      tableRowHover: '#f8fafc',
      badgeGreen: { bg: '#dcfce7', text: '#166534' },
      badgeRed: { bg: '#fee2e2', text: '#991b1b' },
      badgeOrange: { bg: '#ffedd5', text: '#9a3412' },
      badgeBlue: { bg: '#dbeafe', text: '#1e40af' },
      badgeSky: { bg: '#e0f2fe', text: '#0369a1' },
      badgeGray: { bg: '#f1f5f9', text: '#475569' },
      filterSection: '#f8fafc',
      collapseButtonBg: '#e2e8f0',
      collapseButtonHover: '#cbd5e1',
      collapseButtonText: '#0f172a',
      tooltipBg: '#1e293b',
      tooltipText: '#ffffff',
      iconBright: '#f59e0b',
      iconHover: '#d97706',
      sidebarText: '#0f172a',
      sidebarTextMuted: '#64748b',
      pureWhite: '#ffffff',
    },
  };

  const theme = colors[effectiveTheme];

  // ─── MONTH NAMES ─────────────────────────────────────────────────────────────
  const MONTH_NAMES = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December'
  ];

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 10 }, (_, i) => String(currentYear - i));

  // ─── FORMAT DATE DISPLAY ─────────────────────────────────────────────────────
  const formatDateDisplay = () => {
    switch (dateFilterType) {
      case 'single':
        return new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', {
          year: 'numeric', month: isMobile ? 'short' : 'long', day: 'numeric',
        });
      case 'range':
        return isMobile
          ? `${selectedDate} — ${selectedEndDate}`
          : `${new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} — ${new Date(selectedEndDate + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`;
      case 'month':
        return new Date(selectedMonth + '-01').toLocaleDateString('en-US', {
          year: 'numeric', month: isMobile ? 'short' : 'long',
        });
      case 'monthyear':
        return `${isMobile ? MONTH_NAMES[parseInt(selectedMonthOnly, 10) - 1].slice(0, 3) : MONTH_NAMES[parseInt(selectedMonthOnly, 10) - 1]} ${selectedYearForMonth}`;
      case 'thisyear':
        return `Year ${currentYear}`;
      case 'year':
        return `Year ${selectedYear}`;
      default:
        return new Date().toLocaleDateString('en-US', { year: 'numeric', month: isMobile ? 'short' : 'long', day: 'numeric' });
    }
  };

  // ─── COMPACT METRIC CARD (Desktop) ──────────────────────────────────────────
  const CompactMetricCard = ({ icon, label, value, color, bgColor, tooltip }) => (
    <div
      style={{
        background: theme.cardBg,
        borderRadius: '12px',
        padding: '10px 14px',
        boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        minWidth: '160px',
        transition: 'all 0.2s ease',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.transform = 'translateY(-2px)')}
      onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
      title={tooltip}
    >
      <div style={{
        width: '36px', height: '36px', borderRadius: '10px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '20px', background: bgColor || `${color}15`, color,
      }}>{icon}</div>
      <div>
        <div style={{ fontSize: '11px', color: theme.textMuted, fontWeight: '500', marginBottom: '2px' }}>{label}</div>
        <div style={{ fontSize: '16px', fontWeight: '700', color: theme.textPrimary }}>{value}</div>
      </div>
    </div>
  );

  // ─── MOBILE METRIC CARD ──────────────────────────────────────────────────────
  const MobileMetricCard = ({ icon, label, value, color, bgColor }) => (
    <div style={{
      background: '#fff',
      borderRadius: '14px',
      padding: '14px 12px',
      boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
      border: '1px solid #f0f0f0',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      flex: '1 1 calc(50% - 6px)',
      minWidth: '140px',
    }}>
      <div style={{
        width: '38px', height: '38px', borderRadius: '12px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '18px', background: bgColor || `${color}15`,
        flexShrink: 0,
      }}>{icon}</div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '10px', color: '#888', fontWeight: '600', marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
        <div style={{ fontSize: '13px', fontWeight: '700', color: '#111', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</div>
      </div>
    </div>
  );

  // ─── SHARED STYLES ───────────────────────────────────────────────────────────
  const styles = {
    card: {
      background: theme.cardBg,
      border: `1px solid ${theme.cardBorder}`,
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    },
    navItem: (active, collapsed) => ({
      display: 'flex', alignItems: 'center',
      justifyContent: collapsed ? 'center' : 'flex-start',
      gap: collapsed ? '0' : '12px',
      padding: collapsed ? '12px 0' : '8px 14px',
      borderRadius: '8px', cursor: 'pointer', fontSize: '13px',
      fontWeight: active ? '600' : '500',
      color: active ? theme.activeNavText : theme.inactiveNavText,
      background: active ? theme.activeNavBg : 'transparent',
      transition: 'all 0.15s ease', textDecoration: 'none',
      border: 'none', width: '100%',
      textAlign: collapsed ? 'center' : 'left',
      marginBottom: '2px', position: 'relative',
    }),
    sectionHeader: (collapsed) => ({
      fontSize: '10px', fontWeight: '700', textTransform: 'uppercase',
      letterSpacing: '0.08em', color: theme.sectionHeaderText,
      padding: collapsed ? '0' : '0 14px', marginBottom: '6px', marginTop: '10px',
      textAlign: collapsed ? 'center' : 'left',
    }),
    tooltip: {
      position: 'absolute', left: '100%', top: '50%',
      transform: 'translateY(-50%)', marginLeft: '12px',
      padding: '6px 10px', background: theme.tooltipBg, color: theme.tooltipText,
      fontSize: '11px', fontWeight: '500', borderRadius: '6px',
      whiteSpace: 'nowrap', zIndex: 1000, pointerEvents: 'none',
      boxShadow: '0 4px 6px rgba(0,0,0,0.3)', border: `1px solid ${theme.sidebarBorder}`,
    },
    collapseButton: {
      background: theme.collapseButtonBg, border: 'none', color: theme.collapseButtonText,
      fontSize: '18px', fontWeight: 'bold', cursor: 'pointer', padding: '8px 12px',
      borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center',
      transition: 'all 0.2s ease', boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
      minWidth: '36px', height: '36px',
    },
    badge: (type) => {
      const map = {
        green: theme.badgeGreen, red: theme.badgeRed, orange: theme.badgeOrange,
        blue: theme.badgeBlue, sky: theme.badgeSky, gray: theme.badgeGray,
      };
      const c = map[type] || map.gray;
      return {
        display: 'inline-flex', alignItems: 'center', gap: '4px',
        padding: '2px 8px', borderRadius: '20px', fontSize: '10px',
        fontWeight: '600', background: c.bg, color: c.text,
      };
    },
    inputStyle: {
      padding: '8px 10px', border: `1px solid ${isMobile ? '#e2e8f0' : theme.sidebarBorder}`,
      borderRadius: '8px', width: '100%', fontSize: '13px',
      background: '#fff', color: '#111', outline: 'none',
    },
    selectStyle: {
      padding: '8px 10px', border: `1px solid ${isMobile ? '#e2e8f0' : theme.sidebarBorder}`,
      borderRadius: '8px', width: '100%', fontSize: '13px',
      background: '#fff', color: '#111', outline: 'none', cursor: 'pointer',
    },
    label: {
      display: 'block', marginBottom: '5px',
      color: isMobile ? '#555' : theme.sidebarTextMuted,
      fontSize: isMobile ? '12px' : '10px',
      fontWeight: '600',
    },
  };

  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    verifyTokenAndFetchData();
  }, [selectedDate, selectedEndDate, selectedMonth, selectedMonthOnly, selectedYearForMonth, selectedYear, dateFilterType]);

  const verifyTokenAndFetchData = async () => {
    try {
      const params = new URLSearchParams(window.location.search);
      const tokenFromUrl = params.get('token');
      if (!tokenFromUrl) {
        toast.error('No token provided, redirecting to login...');
        navigate('/login');
        return;
      }
      setUrlToken(tokenFromUrl);
      const securityResponse = await fetch(urls.security, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: tokenFromUrl }),
      });
      if (securityResponse.ok) {
        const securityData = await securityResponse.json();
        const themeColor = securityData.colour || '';
        if (!isMobile) {
          if (!themeColor || themeColor.toLowerCase() === 'white' || themeColor.toLowerCase() === 'null') {
            setCurrentTheme('white');
          } else {
            setCurrentTheme('blue');
          }
        }
        if (securityData.message === 'Session valid') {
          setIsTokenVerified(true);
          setEmployeeName(securityData.employee_name);
          setClinicName(securityData.clinic);
          fetchData(tokenFromUrl);
        } else if (securityData.error === 'Session expired') {
          toast.warning('Session expired, redirecting to the dashboard...');
          navigate(`/dashboard?token=${securityData.clinic_session_token}`);
        } else {
          toast.error('Session invalid, redirecting to login...');
          navigate('/login');
        }
      } else {
        throw new Error('Failed to perform security check');
      }
    } catch (error) {
      console.error('Error performing security check:', error);
      toast.error('Error performing security check, redirecting to login...');
      navigate('/login');
    }
  };

  const buildDatePayload = () => {
    switch (dateFilterType) {
      case 'single': return { date: selectedDate };
      case 'range': return { startDate: selectedDate, endDate: selectedEndDate };
      case 'month': return { month: selectedMonth };
      case 'monthyear': return { month: `${selectedYearForMonth}-${selectedMonthOnly}` };
      case 'thisyear': return { year: String(currentYear) };
      case 'year': return { year: selectedYear };
      default: return { date: selectedDate };
    }
  };

  const fetchData = (token) => {
    setIsLoading(true);
    const datePayload = buildDatePayload();
    const payload = { token, ...datePayload };

    fetch(urls.fetchsales3, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(res => res.json())
      .then(data => {
        setSalesData(Array.isArray(data) ? data : []);
        if (data && Array.isArray(data) && data.length > 0 && data[0].Clinic) {
          setClinicName(data[0].Clinic);
        }
      })
      .catch(() => setSalesData([]));

    fetch(urls.fetchexpenses3, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(res => res.json())
      .then(data => {
        if (data && typeof data === 'object') {
          if (Array.isArray(data.expenses)) {
            setExpensesData(data.expenses);
            setTotalDrugsUsed(parseFloat(data.total_drugs_cop || 0));
            setTotalDrugsBought(parseFloat(data.total_drugs_bought_cop || 0));
            setTotalPatients(parseFloat(data.total_patients || 0));
          } else if (Array.isArray(data)) {
            setExpensesData(data);
            setTotalDrugsUsed(0); setTotalDrugsBought(0); setTotalPatients(0);
          } else {
            setExpensesData([]); setTotalDrugsUsed(0); setTotalDrugsBought(0); setTotalPatients(0);
          }
        } else {
          setExpensesData([]); setTotalDrugsUsed(0); setTotalDrugsBought(0); setTotalPatients(0);
        }
      })
      .catch(() => { setExpensesData([]); setTotalDrugsUsed(0); setTotalDrugsBought(0); setTotalPatients(0); })
      .finally(() => setIsLoading(false));
  };

  const isAutomaticEntry = (reason) => reason && (
    reason.includes('Day shift opened automatically by Clinic-pro') ||
    reason.includes('Night shift was closed') ||
    reason.includes('Shift closed automatically by Clinic-pro') ||
    reason.includes('Starting Night shift') ||
    reason.includes('Starting Day shift') ||
    reason.includes('Day shift opened by Clinic Pro') ||
    reason.includes('Night shift opened by Clinic Pro')
  );

  const normalizeCategory = (category) => {
    if (!category) return 'Non-Categorized';
    const lowerCategory = category.toLowerCase().trim();
    if (lowerCategory === 'lab' || lowerCategory === 'laboratory') return 'Laboratory';
    return category.charAt(0).toUpperCase() + category.slice(1);
  };

  const safeSalesData = Array.isArray(salesData) ? salesData : [];
  const safeExpensesData = Array.isArray(expensesData) ? expensesData : [];
  const normalizedSalesData = safeSalesData.map(s => ({ ...s, Category: normalizeCategory(s.Category) }));
  const normalizedExpensesData = safeExpensesData.map(e => ({ ...e, category: normalizeCategory(e.category) }));

  const activeShift = isMobile ? mobileShiftTab : shiftType;

  const daySales = normalizedSalesData.filter(s => s.ShiftType === 'Day' && !isAutomaticEntry(s.Reason));
  const nightSales = normalizedSalesData.filter(s => s.ShiftType === 'Night' && !isAutomaticEntry(s.Reason));
  const dayExpenses = normalizedExpensesData.filter(e => e.ShiftType === 'Day' && !isAutomaticEntry(e.Details));
  const nightExpenses = normalizedExpensesData.filter(e => e.ShiftType === 'Night' && !isAutomaticEntry(e.Details));

  const totalDaySales = daySales.reduce((sum, s) => sum + parseFloat(s.Amount || 0), 0);
  const totalNightSales = nightSales.reduce((sum, s) => sum + parseFloat(s.Amount || 0), 0);
  const totalDayExpenses = dayExpenses.reduce((sum, e) => sum + parseFloat(e.Amount || 0), 0);
  const totalNightExpenses = nightExpenses.reduce((sum, e) => sum + parseFloat(e.Amount || 0), 0);
  const totalSalesCombined = totalDaySales + totalNightSales;
  const totalExpensesCombined = totalDayExpenses + totalNightExpenses;
  const netProfitCombined = totalSalesCombined - totalExpensesCombined - totalDrugsUsed;
  const stockChange = totalDrugsBought - totalDrugsUsed;
  const stockStatus = stockChange >= 0 ? 'added' : 'reduced';

  const filteredSales = normalizedSalesData.filter(s =>
    s.ShiftType === activeShift &&
    (selectedSalesCategory === 'All' || s.Category === selectedSalesCategory) &&
    !isAutomaticEntry(s.Reason)
  );
  const filteredExpenses = normalizedExpensesData.filter(e =>
    e.ShiftType === activeShift &&
    (selectedExpensesCategory === 'All' || e.category === selectedExpensesCategory) &&
    !isAutomaticEntry(e.Details)
  );

  const totalSales = filteredSales.reduce((sum, s) => sum + parseFloat(s.Amount || 0), 0);
  const totalExpenses = filteredExpenses.reduce((sum, e) => sum + parseFloat(e.Amount || 0), 0);

  const salesCategories = [...new Set(normalizedSalesData.map(s => s.Category))];
  const expensesCategories = [...new Set(normalizedExpensesData.map(e => e.category))];

  const salesCategoryTotals = salesCategories.map(cat => ({
    cat,
    salesTotal: normalizedSalesData
      .filter(s => s.ShiftType === activeShift && s.Category === cat && !isAutomaticEntry(s.Reason))
      .reduce((sum, s) => sum + parseFloat(s.Amount || 0), 0),
  })).sort((a, b) => b.salesTotal - a.salesTotal);

  const expensesCategoryTotals = expensesCategories.map(cat => ({
    cat,
    expensesTotal: normalizedExpensesData
      .filter(e => e.ShiftType === activeShift && e.category === cat && !isAutomaticEntry(e.Details))
      .reduce((sum, e) => sum + parseFloat(e.Amount || 0), 0),
  })).sort((a, b) => b.expensesTotal - a.expensesTotal);

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) return 'UGX 0';
    return `UGX ${Math.round(amount).toLocaleString('en-US')}`;
  };

  const formatNumber = (number) => {
    if (number === null || number === undefined || isNaN(number)) return '0';
    return number.toLocaleString('en-US');
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return { date: '', time: '' };
    try {
      const date = new Date(dateTimeString);
      return {
        date: date.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }),
        time: date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }),
      };
    } catch { return { date: '', time: '' }; }
  };

  // ─── DATE INPUTS (shared between sidebar and mobile filter panel) ─────────────
  const renderDateInputs = () => {
    const inputSt = isMobile ? { ...styles.inputStyle } : styles.inputStyle;
    const selectSt = isMobile ? { ...styles.selectStyle } : styles.selectStyle;
    const labelSt = styles.label;

    switch (dateFilterType) {
      case 'single':
        return (
          <div>
            <label style={labelSt}>Select Date:</label>
            <input type="date" value={selectedDate}
              onChange={e => setSelectedDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              style={inputSt} />
          </div>
        );
      case 'range':
        return (
          <div>
            <label style={labelSt}>From:</label>
            <input type="date" value={selectedDate}
              onChange={e => setSelectedDate(e.target.value)}
              max={selectedEndDate}
              style={{ ...inputSt, marginBottom: '8px' }} />
            <label style={labelSt}>To:</label>
            <input type="date" value={selectedEndDate}
              onChange={e => setSelectedEndDate(e.target.value)}
              min={selectedDate} max={new Date().toISOString().split('T')[0]}
              style={inputSt} />
          </div>
        );
      case 'month':
        return (
          <div>
            <label style={labelSt}>Select Month:</label>
            <input type="month" value={selectedMonth}
              onChange={e => setSelectedMonth(e.target.value)}
              max={new Date().toISOString().slice(0, 7)}
              style={inputSt} />
          </div>
        );
      case 'monthyear':
        return (
          <div>
            <label style={labelSt}>Month:</label>
            <select value={selectedMonthOnly}
              onChange={e => setSelectedMonthOnly(e.target.value)}
              style={{ ...selectSt, marginBottom: '8px' }}>
              {MONTH_NAMES.map((m, i) => (
                <option key={i} value={String(i + 1).padStart(2, '0')}>{m}</option>
              ))}
            </select>
            <label style={labelSt}>Year:</label>
            <select value={selectedYearForMonth}
              onChange={e => setSelectedYearForMonth(e.target.value)}
              style={selectSt}>
              {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        );
      case 'thisyear':
        return (
          <div style={{ padding: '8px 0', color: isMobile ? '#111' : theme.sidebarText, fontSize: '13px', fontWeight: '500' }}>
            Showing data for <strong>{currentYear}</strong>
          </div>
        );
      case 'year':
        return (
          <div>
            <label style={labelSt}>Select Year:</label>
            <select value={selectedYear}
              onChange={e => setSelectedYear(e.target.value)}
              style={selectSt}>
              {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        );
      default:
        return null;
    }
  };

  // ─── DESKTOP NAV SECTIONS ─────────────────────────────────────────────────────
  const navSections = [
    {
      label: 'FILTERS',
      items: [
        { id: 'single',    icon: '📅', label: 'Single Date',    action: () => setDateFilterType('single') },
        { id: 'range',     icon: '📆', label: 'Date Range',     action: () => setDateFilterType('range') },
        { id: 'month',     icon: '🗓️', label: 'Month Picker',   action: () => setDateFilterType('month') },
        { id: 'monthyear', icon: '📋', label: 'Month & Year',   action: () => setDateFilterType('monthyear') },
        { id: 'thisyear',  icon: '🌟', label: 'This Year',      action: () => setDateFilterType('thisyear') },
        { id: 'year',      icon: '📊', label: 'Select Year',    action: () => setDateFilterType('year') },
      ],
    },
    {
      label: 'SHIFT',
      items: [
        { id: 'Day',   icon: '☀️', label: 'Day Shift',   action: () => setShiftType('Day') },
        { id: 'Night', icon: '🌙', label: 'Night Shift', action: () => setShiftType('Night') },
      ],
    },
    {
      label: 'VIEW',
      items: [
        { id: 'table', icon: '📊', label: 'Table View', action: () => setShowGraphs(false) },
        { id: 'graph', icon: '📈', label: 'Graph View', action: () => setShowGraphs(true) },
      ],
    },
  ];

  const isNavActive = (item) => {
    if (item.id === dateFilterType) return true;
    if (item.id === 'Day' && shiftType === 'Day') return true;
    if (item.id === 'Night' && shiftType === 'Night') return true;
    if (item.id === 'table' && !showGraphs) return true;
    if (item.id === 'graph' && showGraphs) return true;
    return false;
  };

  // ─── LOADING ────────────────────────────────────────────────────────────────
  if (!isTokenVerified) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: isMobile ? '#fff' : theme.sidebarBg }}>
        <div style={{ textAlign: 'center', color: isMobile ? '#333' : theme.sidebarText }}>
          <div style={{ fontSize: '16px', marginBottom: '15px', fontWeight: '500' }}>Verifying Permission...</div>
          <div style={{ width: '35px', height: '35px', border: `3px solid ${isMobile ? '#e2e8f0' : theme.sidebarBorder}`, borderTop: `3px solid ${isMobile ? '#2563eb' : theme.sidebarText}`, borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto' }}></div>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════════
  // ── MOBILE LAYOUT ─────────────────────────────────────────────────────────────
  // ═══════════════════════════════════════════════════════════════════════════════
  if (isMobile) {
    const FILTER_TYPES = [
      { id: 'single', label: 'Day', icon: '📅' },
      { id: 'range',  label: 'Range', icon: '📆' },
      { id: 'month',  label: 'Month', icon: '🗓️' },
      { id: 'monthyear', label: 'M/Y', icon: '📋' },
      { id: 'year',   label: 'Year', icon: '📊' },
    ];

    const TAB_NAV = [
      { id: 'summary',  label: 'Summary', icon: '📋' },
      { id: 'sales',    label: 'Sales',   icon: '💰' },
      { id: 'expenses', label: 'Expenses',icon: '📤' },
      { id: 'filters',  label: 'Filters', icon: '⚙️' },
    ];

    return (
      <div style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", background: '#f8fafc', minHeight: '100vh', position: 'relative' }}>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
          * { box-sizing: border-box; margin: 0; padding: 0; }
          ::-webkit-scrollbar { width: 4px; height: 4px; }
          ::-webkit-scrollbar-track { background: #f1f5f9; }
          ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
          @keyframes spin { to { transform: rotate(360deg); } }
          @keyframes slideUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
          @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
          .mob-tab-row { display: flex; overflow-x: auto; -webkit-overflow-scrolling: touch; scrollbar-width: none; }
          .mob-tab-row::-webkit-scrollbar { display: none; }
          .mob-row:active { background: #f0f7ff !important; }
          .mob-filter-chip { transition: all 0.15s ease; }
          .mob-filter-chip:active { transform: scale(0.96); }
        `}</style>

        {/* ── TOP BAR ── */}
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
          background: '#ffffff', borderBottom: '1px solid #e2e8f0',
          padding: '10px 14px 8px',
          boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
        }}>
          {/* Clinic + employee row */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
            <div>
              <div style={{ fontSize: '11px', color: '#888', fontWeight: '600', letterSpacing: '0.04em' }}>{clinicName}</div>
              <div style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', lineHeight: 1.2 }}>{employee}</div>
            </div>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              {/* Shift Toggle */}
              <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                {['Day', 'Night'].map(s => (
                  <button key={s} onClick={() => setMobileShiftTab(s)} style={{
                    padding: '5px 10px', border: 'none', cursor: 'pointer', fontSize: '11px', fontWeight: '600',
                    background: mobileShiftTab === s ? '#2563eb' : 'transparent',
                    color: mobileShiftTab === s ? '#fff' : '#475569',
                    transition: 'all 0.15s ease',
                  }}>{s === 'Day' ? '☀️' : '🌙'} {s}</button>
                ))}
              </div>
            </div>
          </div>
          {/* Date display + filter chips */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflowX: 'auto', paddingBottom: '2px' }} className="mob-tab-row">
            {FILTER_TYPES.map(ft => (
              <button key={ft.id} className="mob-filter-chip" onClick={() => setDateFilterType(ft.id)} style={{
                padding: '4px 10px', borderRadius: '20px', border: 'none', cursor: 'pointer',
                fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap',
                background: dateFilterType === ft.id ? '#2563eb' : '#f1f5f9',
                color: dateFilterType === ft.id ? '#fff' : '#475569',
                flexShrink: 0,
              }}>{ft.icon} {ft.label}</button>
            ))}
            <span style={{ fontSize: '11px', color: '#888', whiteSpace: 'nowrap', marginLeft: '4px', flexShrink: 0 }}>
              📆 {formatDateDisplay()}
            </span>
          </div>
        </div>

        {/* ── MAIN SCROLL AREA ── */}
        <div style={{ paddingTop: '90px', paddingBottom: '72px', minHeight: '100vh' }}>

          {isLoading && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px 0' }}>
              <div style={{ width: '32px', height: '32px', border: '3px solid #e2e8f0', borderTop: '3px solid #2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }}></div>
            </div>
          )}

          {!isLoading && (
            <div style={{ animation: 'slideUp 0.25s ease' }}>

              {/* ── SUMMARY TAB ── */}
              {mobileTab === 'summary' && (
                <div style={{ padding: '12px' }}>

                  {/* Metric Grid */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '14px' }}>
                    <MobileMetricCard icon="💰" label="Sales" value={formatCurrency(totalSales)} color="#16a34a" bgColor="#dcfce7" />
                    <MobileMetricCard icon="📤" label="Expenses" value={formatCurrency(totalExpenses)} color="#dc2626" bgColor="#fef2f2" />
                    <MobileMetricCard icon="💊" label="Items Used" value={formatCurrency(totalDrugsUsed)} color="#d97706" bgColor="#fffbeb" />
                    <MobileMetricCard icon="📦" label="Items Bought" value={formatCurrency(totalDrugsBought)} color="#2563eb" bgColor="#eff6ff" />
                  </div>

                  {/* Net Profit Banner */}
                  <div style={{
                    background: netProfitCombined >= 0 ? '#dcfce7' : '#fee2e2',
                    borderRadius: '14px', padding: '14px 16px', marginBottom: '14px',
                    border: `1px solid ${netProfitCombined >= 0 ? '#bbf7d0' : '#fecaca'}`,
                  }}>
                    <div style={{ fontSize: '11px', color: netProfitCombined >= 0 ? '#166534' : '#991b1b', fontWeight: '600', marginBottom: '4px' }}>NET PROFIT</div>
                    <div style={{ fontSize: '22px', fontWeight: '800', color: netProfitCombined >= 0 ? '#15803d' : '#dc2626' }}>
                      {netProfitCombined >= 0 ? '' : '-'}{formatCurrency(Math.abs(netProfitCombined))}
                    </div>
                    <div style={{ fontSize: '11px', color: netProfitCombined >= 0 ? '#166534' : '#991b1b', marginTop: '2px' }}>
                      Sales − Expenses − Drug Purchases
                    </div>
                  </div>

                  {/* Combined Day/Night Breakdown */}
                  <div style={{ background: '#fff', borderRadius: '14px', padding: '14px 16px', marginBottom: '14px', border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: '12px', fontWeight: '700', color: '#0f172a', marginBottom: '10px' }}>DAY / NIGHT BREAKDOWN</div>
                    {[
                      { label: 'Day Sales',    value: totalDaySales,    color: '#16a34a' },
                      { label: 'Night Sales',  value: totalNightSales,  color: '#16a34a' },
                      { label: 'Day Expenses', value: totalDayExpenses, color: '#dc2626' },
                      { label: 'Night Exp.',   value: totalNightExpenses, color: '#dc2626' },
                    ].map((row, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: i < 3 ? '1px solid #f1f5f9' : 'none' }}>
                        <span style={{ fontSize: '12px', color: '#475569', fontWeight: '500' }}>{row.label}</span>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: row.color }}>{formatCurrency(row.value)}</span>
                      </div>
                    ))}
                  </div>

                  {/* Stock Card */}
                  <div style={{ background: '#fff', borderRadius: '14px', padding: '14px 16px', marginBottom: '14px', border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: '12px', fontWeight: '700', color: '#0f172a', marginBottom: '10px' }}>STOCK MOVEMENT</div>
                    {[
                      { label: 'Items Bought', value: totalDrugsBought, color: '#2563eb' },
                      { label: 'Items Used',   value: totalDrugsUsed,   color: '#d97706' },
                      { label: 'Stock Change', value: Math.abs(stockChange), color: stockChange >= 0 ? '#16a34a' : '#dc2626', prefix: stockChange >= 0 ? '+' : '-' },
                    ].map((row, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: i < 2 ? '1px solid #f1f5f9' : 'none' }}>
                        <span style={{ fontSize: '12px', color: '#475569', fontWeight: '500' }}>{row.label}</span>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: row.color }}>{row.prefix || ''}{formatCurrency(row.value)}</span>
                      </div>
                    ))}
                  </div>

                  {/* Insights */}
                  <div style={{ background: '#eff6ff', borderRadius: '14px', padding: '14px 16px', border: '1px solid #dbeafe' }}>
                    <div style={{ fontSize: '12px', fontWeight: '700', color: '#1e40af', marginBottom: '10px' }}>📊 INSIGHTS</div>
                    {[
                      { label: 'Profit Margin', value: totalSalesCombined > 0 ? `${((totalSalesCombined - totalDrugsUsed) / totalSalesCombined * 100).toFixed(1)}%` : '0%' },
                      { label: 'Expense Ratio', value: totalSalesCombined > 0 ? `${(totalExpensesCombined / totalSalesCombined * 100).toFixed(1)}%` : '0%' },
                      { label: 'Drug Cost Ratio', value: totalSalesCombined > 0 ? `${(totalDrugsUsed / totalSalesCombined * 100).toFixed(1)}%` : '0%' },
                      { label: 'Day Sales Share', value: totalSalesCombined > 0 ? `${(totalDaySales / totalSalesCombined * 100).toFixed(1)}%` : '0%' },
                      { label: 'Night Sales Share', value: totalSalesCombined > 0 ? `${(totalNightSales / totalSalesCombined * 100).toFixed(1)}%` : '0%' },
                      { label: 'Purchase-to-Use', value: totalDrugsUsed > 0 ? `${((totalDrugsBought / totalDrugsUsed) * 100).toFixed(1)}%` : '0%' },
                    ].map((row, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: i < 5 ? '1px solid #dbeafe' : 'none' }}>
                        <span style={{ fontSize: '12px', color: '#3b82f6', fontWeight: '500' }}>{row.label}</span>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: '#1e40af' }}>{row.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── SALES TAB ── */}
              {mobileTab === 'sales' && (
                <div style={{ padding: '12px' }}>
                  {/* Category Filter */}
                  <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', paddingBottom: '4px', marginBottom: '12px' }} className="mob-tab-row">
                    {['All', ...salesCategories].map(cat => (
                      <button key={cat} onClick={() => setSelectedSalesCategory(cat)} className="mob-filter-chip" style={{
                        padding: '5px 12px', borderRadius: '20px', border: 'none', cursor: 'pointer',
                        fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap',
                        background: selectedSalesCategory === cat ? '#16a34a' : '#f1f5f9',
                        color: selectedSalesCategory === cat ? '#fff' : '#475569', flexShrink: 0,
                      }}>{cat}</button>
                    ))}
                  </div>

                  {/* Total */}
                  <div style={{ background: '#dcfce7', borderRadius: '12px', padding: '10px 14px', marginBottom: '12px', border: '1px solid #bbf7d0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '12px', fontWeight: '600', color: '#166534' }}>Total ({mobileShiftTab} Shift)</span>
                    <span style={{ fontSize: '16px', fontWeight: '800', color: '#15803d' }}>{formatCurrency(totalSales)}</span>
                  </div>

                  {/* Category Summary */}
                  <div style={{ background: '#fff', borderRadius: '14px', marginBottom: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <div style={{ padding: '10px 14px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>By Category</div>
                    {salesCategoryTotals.filter(c => c.salesTotal > 0).map((c, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid #f1f5f9' }}>
                        <span style={{ fontSize: '13px', color: '#374151', fontWeight: '500' }}>{c.cat}</span>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: '#16a34a' }}>{formatCurrency(c.salesTotal)}</span>
                      </div>
                    ))}
                  </div>

                  {/* Individual Entries */}
                  <div style={{ background: '#fff', borderRadius: '14px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <div style={{ padding: '10px 14px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Transactions</div>
                    {filteredSales.filter(s => s.Amount > 0).length === 0 && (
                      <div style={{ padding: '24px', textAlign: 'center', fontSize: '13px', color: '#94a3b8' }}>No sales data</div>
                    )}
                    {filteredSales.filter(s => s.Amount > 0).map((s, i) => {
                      const { date, time } = formatDateTime(s.CreatedAt);
                      return (
                        <div key={i} className="mob-row" style={{ padding: '11px 14px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '13px', color: '#0f172a', fontWeight: '500', marginBottom: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.Reason?.substring(0, 30) || '—'}</div>
                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                              <span style={{ fontSize: '10px', color: '#94a3b8' }}>{date} {time}</span>
                              <span style={{ fontSize: '10px', background: '#f1f5f9', color: '#475569', padding: '1px 6px', borderRadius: '10px', fontWeight: '600' }}>{s.Category}</span>
                            </div>
                          </div>
                          <span style={{ fontSize: '14px', fontWeight: '700', color: '#16a34a', marginLeft: '10px', flexShrink: 0 }}>{formatCurrency(s.Amount)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ── EXPENSES TAB ── */}
              {mobileTab === 'expenses' && (
                <div style={{ padding: '12px' }}>
                  {/* Category Filter */}
                  <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', paddingBottom: '4px', marginBottom: '12px' }} className="mob-tab-row">
                    {['All', ...expensesCategories].map(cat => (
                      <button key={cat} onClick={() => setSelectedExpensesCategory(cat)} className="mob-filter-chip" style={{
                        padding: '5px 12px', borderRadius: '20px', border: 'none', cursor: 'pointer',
                        fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap',
                        background: selectedExpensesCategory === cat ? '#dc2626' : '#f1f5f9',
                        color: selectedExpensesCategory === cat ? '#fff' : '#475569', flexShrink: 0,
                      }}>{cat}</button>
                    ))}
                  </div>

                  {/* Total */}
                  <div style={{ background: '#fee2e2', borderRadius: '12px', padding: '10px 14px', marginBottom: '12px', border: '1px solid #fecaca', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '12px', fontWeight: '600', color: '#991b1b' }}>Total ({mobileShiftTab} Shift)</span>
               
               
                 
          
          
             <span style={{ fontSize: '16px', fontWeight: '800', color: '#dc2626' }}>{formatCurrency(totalExpenses)}</span>
                  </div>

                  {/* Category Summary */}
                  <div style={{ background: '#fff', borderRadius: '14px', marginBottom: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <div style={{ padding: '10px 14px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>By Category</div>
                    {expensesCategoryTotals.filter(c => c.expensesTotal > 0).map((c, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid #f1f5f9' }}>
                        <span style={{ fontSize: '13px', color: '#374151', fontWeight: '500' }}>{c.cat}</span>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: '#dc2626' }}>{formatCurrency(c.expensesTotal)}</span>
                      </div>
                    ))}
                  </div>

                  {/* Individual Entries */}
                  <div style={{ background: '#fff', borderRadius: '14px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <div style={{ padding: '10px 14px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Transactions</div>
                    {filteredExpenses.filter(e => e.Amount > 0).length === 0 && (
                      <div style={{ padding: '24px', textAlign: 'center', fontSize: '13px', color: '#94a3b8' }}>No expenses data</div>
                    )}
                    {filteredExpenses.filter(e => e.Amount > 0).map((e, i) => {
                      const { date, time } = formatDateTime(e.created_at);
                      return (
                        <div key={i} className="mob-row" style={{ padding: '11px 14px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '13px', color: '#0f172a', fontWeight: '500', marginBottom: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.Details?.substring(0, 30) || '—'}</div>
                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                              <span style={{ fontSize: '10px', color: '#94a3b8' }}>{date} {time}</span>
                              <span style={{ fontSize: '10px', background: '#f1f5f9', color: '#475569', padding: '1px 6px', borderRadius: '10px', fontWeight: '600' }}>{e.category}</span>
                            </div>
                          </div>
                          <span style={{ fontSize: '14px', fontWeight: '700', color: '#dc2626', marginLeft: '10px', flexShrink: 0 }}>{formatCurrency(e.Amount)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ── FILTERS TAB ── */}
              {mobileTab === 'filters' && (
                <div style={{ padding: '16px 12px' }}>
                  <div style={{ background: '#fff', borderRadius: '16px', padding: '16px', border: '1px solid #e2e8f0', marginBottom: '12px' }}>
                    <div style={{ fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Date Filter Type</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '16px' }}>
                      {[
                        { id: 'single',    label: 'Single Day', icon: '📅' },
                        { id: 'range',     label: 'Date Range', icon: '📆' },
                        { id: 'month',     label: 'Month',      icon: '🗓️' },
                        { id: 'monthyear', label: 'Month/Year', icon: '📋' },
                        { id: 'thisyear',  label: 'This Year',  icon: '🌟' },
                        { id: 'year',      label: 'Select Year',icon: '📊' },
                      ].map(ft => (
                        <button key={ft.id} onClick={() => setDateFilterType(ft.id)} style={{
                          padding: '10px 8px', borderRadius: '10px', border: 'none', cursor: 'pointer',
                          fontSize: '12px', fontWeight: '600', textAlign: 'center',
                          background: dateFilterType === ft.id ? '#2563eb' : '#f1f5f9',
                          color: dateFilterType === ft.id ? '#fff' : '#475569',
                          transition: 'all 0.15s ease',
                        }}>{ft.icon} {ft.label}</button>
                      ))}
                    </div>

                    <div style={{ fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Date Selection</div>
                    {renderDateInputs()}
                  </div>

                  <div style={{ background: '#fff', borderRadius: '16px', padding: '16px', border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Employee</div>
                    <div style={{ fontSize: '14px', color: '#0f172a', fontWeight: '600' }}>{employeeName || employee}</div>
                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>{clinicName}</div>
                  </div>
                </div>
              )}

            </div>
          )}
        </div>

        {/* ── BOTTOM NAV BAR ── */}
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100,
          background: '#ffffff', borderTop: '1px solid #e2e8f0',
          display: 'flex', boxShadow: '0 -2px 10px rgba(0,0,0,0.06)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}>
          {TAB_NAV.map(tab => (
            <button key={tab.id} onClick={() => setMobileTab(tab.id)} style={{
              flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', padding: '8px 4px 10px', border: 'none', cursor: 'pointer',
              background: 'transparent', gap: '2px', transition: 'all 0.15s ease',
            }}>
              <span style={{ fontSize: '20px', lineHeight: 1 }}>{tab.icon}</span>
              <span style={{ fontSize: '10px', fontWeight: mobileTab === tab.id ? '700' : '500', color: mobileTab === tab.id ? '#2563eb' : '#94a3b8' }}>{tab.label}</span>
              {mobileTab === tab.id && (
                <span style={{ width: '20px', height: '3px', background: '#2563eb', borderRadius: '2px', marginTop: '2px' }}></span>
              )}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════════
  // ── DESKTOP LAYOUT (original, unchanged) ─────────────────────────────────────
  // ═══════════════════════════════════════════════════════════════════════════════
  return (
    <div style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", background: theme.pureWhite, minHeight: '100vh', position: 'relative' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: ${theme.tableHeader}; }
        ::-webkit-scrollbar-thumb { background: ${theme.textMuted}; border-radius: 4px; }
        ::-webkit-scrollbar-thumb:hover { background: ${theme.textSecondary}; }
        .nav-item:hover { background: ${theme.navHoverBg} !important; color: ${theme.activeNavText} !important; }
        .nav-item:hover .nav-icon { color: ${theme.iconHover} !important; transform: scale(1.1); }
        .table-row:hover td { background: ${theme.tableRowHover}; }
        .fade-in { animation: fadeIn 0.3s ease; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes spin { to { transform: rotate(360deg); } }
        table { border-collapse: collapse; width: 100%; }
        .collapse-btn:hover { background: ${theme.collapseButtonHover} !important; transform: scale(1.05); box-shadow: 0 4px 12px ${effectiveTheme === 'blue' ? 'rgba(37, 99, 235, 0.3)' : 'rgba(0,0,0,0.1)'}; }
        .nav-icon { color: ${theme.iconBright}; font-size: 18px; transition: all 0.2s ease; }
        .active-tab { background: ${theme.activeNavBg} !important; border-radius: 8px !important; }
        .active-tab .nav-icon { color: ${effectiveTheme === 'blue' ? '#ffffff' : theme.iconBright} !important; }
        .category-select { width: 140px !important; padding: 6px 8px !important; font-size: 11px !important; }
      `}</style>

      <Topbar token={urlToken} themeColor={effectiveTheme} />

      <div style={{ display: 'flex', minHeight: 'calc(100vh - 60px)', marginTop: '60px' }}>
        {/* ── SIDEBAR ── */}
        <aside style={{
          width: sidebarCollapsed ? '80px' : '250px',
          background: theme.sidebarBg,
          display: 'flex', flexDirection: 'column', flexShrink: 0,
          borderRight: `1px solid ${theme.sidebarBorder}`,
          position: 'fixed', top: '60px', height: 'calc(100vh - 60px)',
          overflowY: 'auto', boxShadow: '2px 0 8px rgba(0,0,0,0.1)',
          transition: 'width 0.3s ease', zIndex: 900,
        }}>
          <div style={{
            padding: sidebarCollapsed ? '16px 8px' : '16px',
            borderBottom: `2px solid ${theme.sidebarBorder}`,
            display: 'flex', flexDirection: sidebarCollapsed ? 'column' : 'row',
            alignItems: 'center', justifyContent: 'space-between',
            background: theme.filterSection, minHeight: sidebarCollapsed ? '100px' : '70px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, order: sidebarCollapsed ? 2 : 1, marginTop: sidebarCollapsed ? '8px' : 0 }}>
              <div style={{ width: '40px', height: '40px', background: theme.activeNavBg, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', flexShrink: 0, color: theme.activeNavText, fontWeight: 'bold', boxShadow: '0 4px 10px rgba(37, 99, 235, 0.3)' }}>CP</div>
              {!sidebarCollapsed && (
                <div style={{ overflow: 'hidden' }}>
                  <div style={{ color: theme.sidebarText, fontWeight: '700', fontSize: '15px', letterSpacing: '-0.01em', whiteSpace: 'nowrap' }}>ClinicPro</div>
                  <div style={{ color: theme.sidebarTextMuted, fontSize: '10px', fontWeight: '500', marginTop: '2px', whiteSpace: 'nowrap' }}>Sales History</div>
                </div>
              )}
            </div>
            <button className="collapse-btn" onClick={() => setSidebarCollapsed(!sidebarCollapsed)} style={{ ...styles.collapseButton, background: theme.collapseButtonBg, border: effectiveTheme === 'blue' ? '2px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.1)', color: theme.collapseButtonText, width: '38px', height: '38px', borderRadius: '8px', fontSize: '18px', order: sidebarCollapsed ? 1 : 2, marginLeft: sidebarCollapsed ? 0 : 'auto', flexShrink: 0 }} title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
              {sidebarCollapsed ? '→' : '←'}
            </button>
          </div>

          <nav style={{ padding: sidebarCollapsed ? '12px 0' : '12px 10px', flex: 1 }}>
            {navSections.map(section => (
              <div key={section.label} style={{ marginBottom: '12px' }}>
                {!sidebarCollapsed && <div style={styles.sectionHeader(sidebarCollapsed)}>{section.label}</div>}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                  {section.items.map(item => {
                    const active = isNavActive(item);
                    return (
                      <button key={item.id} className={`nav-item ${active ? 'active-tab' : ''}`}
                        onClick={() => item.action && item.action()}
                        onMouseEnter={() => setHoveredNavItem(item.id)}
                        onMouseLeave={() => setHoveredNavItem(null)}
                        style={{ ...styles.navItem(active, sidebarCollapsed), background: active ? theme.activeNavBg : 'transparent', padding: sidebarCollapsed ? '10px 0' : '8px 12px', margin: sidebarCollapsed ? '0' : '0 0 2px 0', color: active ? theme.activeNavText : theme.inactiveNavText }}>
                        <span className="nav-icon" style={{ fontSize: '16px', width: '18px', textAlign: 'center', color: active ? (effectiveTheme === 'blue' ? '#ffffff' : theme.iconBright) : theme.iconBright }}>{item.icon}</span>
                        {!sidebarCollapsed && <span style={{ fontWeight: '600', color: active ? theme.activeNavText : theme.inactiveNavText }}>{item.label}</span>}
                        {sidebarCollapsed && hoveredNavItem === item.id && <div style={styles.tooltip}>{item.label}</div>}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>

          {!sidebarCollapsed && (
            <div style={{ padding: '12px 14px', borderTop: `1px solid ${theme.sidebarBorder}`, background: theme.filterSection }}>
              {renderDateInputs()}
            </div>
          )}

          <div style={{ padding: sidebarCollapsed ? '12px 0' : '12px 16px', borderTop: `1px solid ${theme.sidebarBorder}`, background: theme.filterSection, textAlign: sidebarCollapsed ? 'center' : 'left' }}>
            {!sidebarCollapsed ? (
              <>
                <div style={{ fontSize: '10px', color: theme.sidebarTextMuted, marginBottom: '4px', fontWeight: '600', letterSpacing: '0.05em' }}>{clinicName}</div>
                <div style={{ fontSize: '11px', color: theme.sidebarText, fontWeight: '500' }}>{employeeName || employee}</div>
                <div style={{ fontSize: '9px', color: theme.sidebarTextMuted, marginTop: '4px' }}>{shiftType} Shift</div>
              </>
            ) : (
              <div style={{ fontSize: '12px', color: theme.sidebarText, fontWeight: '500', cursor: 'pointer', position: 'relative' }}
                onMouseEnter={() => setHoveredNavItem('user')} onMouseLeave={() => setHoveredNavItem(null)}>
                👤
                {hoveredNavItem === 'user' && <div style={{ ...styles.tooltip, left: '100%' }}>{employeeName || employee} - {clinicName} ({shiftType})</div>}
              </div>
            )}
          </div>
        </aside>

        {/* ── MAIN CONTENT ── */}
        <main style={{ flex: 1, marginLeft: sidebarCollapsed ? '80px' : '250px', padding: '16px', background: theme.mainBg, minHeight: 'calc(100vh - 60px)', transition: 'margin-left 0.3s ease', display: 'flex', flexDirection: 'column' }}>
          {/* HEADER BAR */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', padding: '10px 16px', background: theme.cardBg, borderRadius: '12px', border: `1px solid ${theme.cardBorder}`, boxShadow: '0 2px 6px rgba(0,0,0,0.04)', flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ minWidth: '160px' }}>
              <h2 style={{ margin: 0, color: theme.textPrimary, fontSize: '18px', fontWeight: '600', lineHeight: '1.2', marginBottom: '4px' }}>{employee}</h2>
              <div style={{ fontSize: '12px', color: theme.textSecondary, fontWeight: '500' }}>{formatDateDisplay()}</div>
              <div style={{ fontSize: '11px', color: theme.textMuted, display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' }}>
                <span style={styles.badge(shiftType === 'Day' ? 'sky' : 'blue')}>{shiftType} Shift</span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flex: 2, flexWrap: 'wrap' }}>
              <CompactMetricCard icon="💰" label="Total Sales" value={formatCurrency(totalSales)} color={theme.accent} bgColor={theme.accentLight} tooltip="Total revenue from sales" />
              <CompactMetricCard icon="📤" label="Total Expenses" value={formatCurrency(totalExpenses)} color={theme.danger} bgColor={theme.dangerLight} tooltip="Total operational expenses (excluding drugs)" />
              <CompactMetricCard icon="💊" label="Purchases" value={`${formatCurrency(totalDrugsBought)} / ${formatCurrency(totalDrugsUsed)}`} color={stockChange >= 0 ? theme.accent : theme.warning} bgColor={stockChange >= 0 ? theme.accentLight : theme.warningLight} tooltip={`Stock ${stockStatus}: ${formatCurrency(Math.abs(stockChange))}`} />
            </div>
            <div style={{ minWidth: '160px', display: 'flex', justifyContent: 'flex-end' }}>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '10px', color: theme.textMuted, marginBottom: '4px', fontWeight: '500' }}>VIEW:</div>
                <div style={{ display: 'inline-flex', background: theme.tableHeader, borderRadius: '8px', overflow: 'hidden', border: `1px solid ${theme.cardBorder}` }}>
                  <button onClick={() => setViewType('detailed')} style={{ padding: '6px 12px', background: viewType === 'detailed' ? theme.activeNavBg : 'transparent', color: viewType === 'detailed' ? (effectiveTheme === 'blue' ? '#fff' : theme.activeNavText) : theme.textPrimary, border: 'none', cursor: 'pointer', fontSize: '11px', fontWeight: viewType === 'detailed' ? '600' : '500' }}>Detailed</button>
                  <button onClick={() => setViewType('category')} style={{ padding: '6px 12px', background: viewType === 'category' ? theme.activeNavBg : 'transparent', color: viewType === 'category' ? (effectiveTheme === 'blue' ? '#fff' : theme.activeNavText) : theme.textPrimary, border: 'none', cursor: 'pointer', fontSize: '11px', fontWeight: viewType === 'category' ? '600' : '500' }}>Category</button>
                </div>
              </div>
            </div>
          </div>

          {isLoading && (
            <div style={{ textAlign: 'center', padding: '40px', color: theme.textSecondary }}>
              <div style={{ width: '36px', height: '36px', border: `3px solid ${theme.tableBorder}`, borderTop: `3px solid ${theme.activeNavBg}`, borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 12px auto' }}></div>
              <p style={{ fontSize: '13px' }}>Loading data...</p>
            </div>
          )}

          {showGraphs && !isLoading && (
            <div style={{ marginTop: '0', flex: 1, minHeight: 0 }}>
              <GraphComponent salesData={salesData} expensesData={expensesData} token={urlToken} />
            </div>
          )}

          {!showGraphs && !isLoading && (
            <>
              {viewType === 'detailed' && (
                <div style={{ display: 'flex', gap: '16px', height: '450px', minHeight: 0 }}>
                  {/* Sales Table */}
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: theme.cardBg, borderRadius: '12px', border: `1px solid ${theme.cardBorder}`, overflow: 'hidden' }}>
                    <div style={{ padding: '10px 14px', borderBottom: `1px solid ${theme.tableBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <h3 style={{ color: theme.textPrimary, fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}><span>💰</span> SALES</h3>
                      <select value={selectedSalesCategory} onChange={e => setSelectedSalesCategory(e.target.value)} className="category-select" style={{ padding: '5px 8px', borderRadius: '6px', border: `1px solid ${theme.cardBorder}`, fontSize: '10px', color: theme.textPrimary, background: theme.cardBg, outline: 'none', cursor: 'pointer', width: '130px' }}>
                        <option value="All">All Categories</option>
                        {salesCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                      </select>
                    </div>
                    <div style={{ flex: 1, overflow: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                        <thead style={{ background: theme.tableHeader, position: 'sticky', top: 0, zIndex: 10 }}>
                          <tr>{['Date','Time','Amount','Reason','Category','By'].map(h => <th key={h} style={{ padding: '8px 6px', borderBottom: `1px solid ${theme.tableBorder}`, fontWeight: '600', color: theme.textSecondary }}>{h}</th>)}</tr>
                        </thead>
                        <tbody>
                          {filteredSales.filter(s => s.Amount > 0).map((s, i) => {
                            const { date, time } = formatDateTime(s.CreatedAt);
                            return (
                              <tr key={i} className="table-row">
                                <td style={{ padding: '6px', borderBottom: `1px solid ${theme.tableBorder}`, fontSize: '10px', color: theme.textSecondary }}>{date}</td>
                                <td style={{ padding: '6px', borderBottom: `1px solid ${theme.tableBorder}`, fontSize: '10px', color: theme.textSecondary }}>{time}</td>
                                <td style={{ padding: '6px', borderBottom: `1px solid ${theme.tableBorder}`, color: theme.accent, fontSize: '10px', fontWeight: '600' }}>{formatCurrency(s.Amount)}</td>
                                <td style={{ padding: '6px', borderBottom: `1px solid ${theme.tableBorder}`, fontSize: '10px', color: theme.textSecondary }}>{s.Reason?.substring(0, 20)}...</td>
                                <td style={{ padding: '6px', borderBottom: `1px solid ${theme.tableBorder}`, fontSize: '10px', color: theme.textSecondary }}><span style={styles.badge('gray')}>{s.Category}</span></td>
                                <td style={{ padding: '6px', borderBottom: `1px solid ${theme.tableBorder}`, fontSize: '10px', color: theme.textSecondary }}>{s.ServedBy || 'N/A'}</td>
                              </tr>
                            );
                          })}
                          {filteredSales.filter(s => s.Amount > 0).length === 0 && <tr><td colSpan="6" style={{ padding: '20px', textAlign: 'center', fontSize: '11px', color: theme.textMuted }}>No sales data</td></tr>}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Expenses Table */}
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: theme.cardBg, borderRadius: '12px', border: `1px solid ${theme.cardBorder}`, overflow: 'hidden' }}>
                    <div style={{ padding: '10px 14px', borderBottom: `1px solid ${theme.tableBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <h3 style={{ color: theme.textPrimary, fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}><span>📤</span> EXPENSES</h3>
                      <select value={selectedExpensesCategory} onChange={e => setSelectedExpensesCategory(e.target.value)} className="category-select" style={{ padding: '5px 8px', borderRadius: '6px', border: `1px solid ${theme.cardBorder}`, fontSize: '10px', color: theme.textPrimary, background: theme.cardBg, outline: 'none', cursor: 'pointer', width: '130px' }}>
                        <option value="All">All Categories</option>
                        {expensesCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                      </select>
                    </div>
                    <div style={{ flex: 1, overflow: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                        <thead style={{ background: theme.tableHeader, position: 'sticky', top: 0, zIndex: 10 }}>
                          <tr>{['Date','Time','Amount','Details','Category','By'].map(h => <th key={h} style={{ padding: '8px 6px', borderBottom: `1px solid ${theme.tableBorder}`, fontWeight: '600', color: theme.textSecondary }}>{h}</th>)}</tr>
                        </thead>
                        <tbody>
                          {filteredExpenses.filter(e => e.Amount > 0).map((e, i) => {
                            const { date, time } = formatDateTime(e.created_at);
                            return (
                              <tr key={i} className="table-row">
                                <td style={{ padding: '6px', borderBottom: `1px solid ${theme.tableBorder}`, fontSize: '10px', color: theme.textSecondary }}>{date}</td>
                                <td style={{ padding: '6px', borderBottom: `1px solid ${theme.tableBorder}`, fontSize: '10px', color: theme.textSecondary }}>{time}</td>
                                <td style={{ padding: '6px', borderBottom: `1px solid ${theme.tableBorder}`, color: theme.danger, fontSize: '10px', fontWeight: '600' }}>{formatCurrency(e.Amount)}</td>
                                <td style={{ padding: '6px', borderBottom: `1px solid ${theme.tableBorder}`, fontSize: '10px', color: theme.textSecondary }}>{e.Details?.substring(0, 20)}...</td>
                                <td style={{ padding: '6px', borderBottom: `1px solid ${theme.tableBorder}`, fontSize: '10px', color: theme.textSecondary }}><span style={styles.badge('gray')}>{e.category}</span></td>
                                <td style={{ padding: '6px', borderBottom: `1px solid ${theme.tableBorder}`, fontSize: '10px', color: theme.textSecondary }}>{e.ServedBy || 'N/A'}</td>
                              </tr>
                            );
                          })}
                          {filteredExpenses.filter(e => e.Amount > 0).length === 0 && <tr><td colSpan="6" style={{ padding: '20px', textAlign: 'center', fontSize: '11px', color: theme.textMuted }}>No expenses data</td></tr>}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {viewType === 'category' && (
                <div style={{ height: '450px', minHeight: 0 }}>
                  <div style={{ ...styles.card, padding: '16px', height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <h3 style={{ color: theme.textPrimary, marginBottom: '12px', textAlign: 'center', fontWeight: '600', fontSize: '14px' }}>
                      CATEGORY SUMMARY — {shiftType} · {formatDateDisplay()}
                    </h3>
                    <div style={{ display: 'flex', gap: '16px', flex: 1, minHeight: 0 }}>
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <h4 style={{ color: theme.textPrimary, marginBottom: '8px', fontWeight: '600', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}><span>💰</span> SALES BY CATEGORY</h4>
                        <div style={{ flex: 1, overflow: 'auto', background: theme.cardBg, borderRadius: '8px', border: `1px solid ${theme.cardBorder}` }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                            <thead style={{ background: theme.tableHeader, position: 'sticky', top: 0 }}>
                              <tr>
                                <th style={{ padding: '8px', borderBottom: `1px solid ${theme.tableBorder}`, fontWeight: '600', color: theme.textSecondary }}>Category</th>
                                <th style={{ padding: '8px', borderBottom: `1px solid ${theme.tableBorder}`, fontWeight: '600', color: theme.textSecondary }}>Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {salesCategoryTotals.filter(c => c.salesTotal > 0).map((c, i) => (
                                <tr key={i} className="table-row">
                                  <td style={{ padding: '6px', borderBottom: `1px solid ${theme.tableBorder}`, fontSize: '10px', color: theme.textSecondary }}>{c.cat}</td>
                                  <td style={{ padding: '6px', borderBottom: `1px solid ${theme.tableBorder}`, color: theme.accent, fontSize: '10px', fontWeight: '600' }}>{formatCurrency(c.salesTotal)}</td>
                                </tr>
                              ))}
                              {totalSales > 0 && (
                                <tr style={{ background: theme.tableHeader }}>
                                  <td style={{ padding: '6px', fontSize: '10px', fontWeight: '600', color: theme.textPrimary }}>TOTAL</td>
                                  <td style={{ padding: '6px', color: theme.accent, fontSize: '10px', fontWeight: '700' }}>{formatCurrency(totalSales)}</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <h4 style={{ color: theme.textPrimary, marginBottom: '8px', fontWeight: '600', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}><span>📤</span> EXPENSES BY CATEGORY</h4>
                        <div style={{ flex: 1, overflow: 'auto', background: theme.cardBg, borderRadius: '8px', border: `1px solid ${theme.cardBorder}` }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                            <thead style={{ background: theme.tableHeader, position: 'sticky', top: 0 }}>
                              <tr>
                                <th style={{ padding: '8px', borderBottom: `1px solid ${theme.tableBorder}`, fontWeight: '600', color: theme.textSecondary }}>Category</th>
                                <th style={{ padding: '8px', borderBottom: `1px solid ${theme.tableBorder}`, fontWeight: '600', color: theme.textSecondary }}>Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {expensesCategoryTotals.filter(c => c.expensesTotal > 0).map((c, i) => (
                                <tr key={i} className="table-row">
                                  <td style={{ padding: '6px', borderBottom: `1px solid ${theme.tableBorder}`, fontSize: '10px', color: theme.textSecondary }}>{c.cat}</td>
                                  <td style={{ padding: '6px', borderBottom: `1px solid ${theme.tableBorder}`, color: theme.danger, fontSize: '10px', fontWeight: '600' }}>{formatCurrency(c.expensesTotal)}</td>
                                </tr>
                              ))}
                              {totalExpenses > 0 && (
                                <tr style={{ background: theme.tableHeader }}>
                                  <td style={{ padding: '6px', fontSize: '10px', fontWeight: '600', color: theme.textPrimary }}>TOTAL</td>
                                  <td style={{ padding: '6px', color: theme.danger, fontSize: '10px', fontWeight: '700' }}>{formatCurrency(totalExpenses)}</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Combined Summary */}
              <div style={{ marginTop: '16px', ...styles.card, padding: '16px' }}>
                <h3 style={{ textAlign: 'center', margin: '0 0 12px 0', color: theme.textPrimary, fontSize: '16px', fontWeight: '600' }}>COMBINED SUMMARY</h3>
                <div style={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: '12px', marginBottom: '12px' }}>
                  <div style={{ textAlign: 'center' }}><div style={{ color: theme.textMuted, fontSize: '10px', fontWeight: '500' }}>Day Sales</div><div style={{ fontSize: '13px', color: theme.accent, fontWeight: '600' }}>{formatCurrency(totalDaySales)}</div></div>
                  <div style={{ textAlign: 'center' }}><div style={{ color: theme.textMuted, fontSize: '10px', fontWeight: '500' }}>Night Sales</div><div style={{ fontSize: '13px', color: theme.accent, fontWeight: '600' }}>{formatCurrency(totalNightSales)}</div></div>
                  <div style={{ textAlign: 'center' }}><div style={{ color: theme.textMuted, fontSize: '10px', fontWeight: '500' }}>Day Expenses</div><div style={{ fontSize: '13px', color: theme.danger, fontWeight: '600' }}>{formatCurrency(totalDayExpenses)}</div></div>
                  <div style={{ textAlign: 'center' }}><div style={{ color: theme.textMuted, fontSize: '10px', fontWeight: '500' }}>Night Expenses</div><div style={{ fontSize: '13px', color: theme.danger, fontWeight: '600' }}>{formatCurrency(totalNightExpenses)}</div></div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: '12px', paddingTop: '10px', borderTop: `1px solid ${theme.tableBorder}` }}>
                  <div style={{ textAlign: 'center' }}><div style={{ color: theme.textMuted, fontSize: '10px', fontWeight: '500' }}>Total Sales</div><div style={{ fontSize: '15px', color: theme.accent, fontWeight: '700' }}>{formatCurrency(totalSalesCombined)}</div></div>
                  <div style={{ textAlign: 'center' }}><div style={{ color: theme.textMuted, fontSize: '10px', fontWeight: '500' }}>Total Expenses</div><div style={{ fontSize: '15px', color: theme.danger, fontWeight: '700' }}>{formatCurrency(totalExpensesCombined)}</div></div>
                  <div style={{ textAlign: 'center' }}><div style={{ color: theme.textMuted, fontSize: '10px', fontWeight: '500' }}>Items Bought</div><div style={{ fontSize: '15px', color: theme.info, fontWeight: '700' }}>{formatCurrency(totalDrugsBought)}</div></div>
                  <div style={{ textAlign: 'center' }}><div style={{ color: theme.textMuted, fontSize: '10px', fontWeight: '500' }}>Items Used</div><div style={{ fontSize: '15px', color: theme.warning, fontWeight: '700' }}>{formatCurrency(totalDrugsUsed)}</div></div>
                  <div style={{ textAlign: 'center' }}><div style={{ color: theme.textMuted, fontSize: '10px', fontWeight: '500' }}>Stock Change</div><div style={{ fontSize: '15px', color: stockChange >= 0 ? theme.accent : theme.danger, fontWeight: '700' }}>{stockChange >= 0 ? '+' : '-'}{formatCurrency(Math.abs(stockChange))}</div></div>
                  <div style={{ textAlign: 'center' }}><div style={{ color: theme.textMuted, fontSize: '10px', fontWeight: '500' }}>Net Profit</div><div style={{ fontSize: '15px', color: netProfitCombined >= 0 ? theme.accent : theme.danger, fontWeight: '700' }}>{formatCurrency(netProfitCombined)}</div></div>
                </div>
                <div style={{ marginTop: '12px', padding: '12px', background: theme.infoLight, borderRadius: '8px', border: `1px solid ${theme.cardBorder}`, fontSize: '11px' }}>
                  <h4 style={{ color: theme.textPrimary, fontWeight: '600', marginBottom: '8px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}><span>📊</span> Insights</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '8px' }}>
                    <div style={{ color: theme.textSecondary }}>• Gross Profit (Sales - Purchases): {formatCurrency(totalSalesCombined - totalDrugsUsed)}</div>
                    <div style={{ color: theme.textSecondary }}>• Profit Margin: {totalSalesCombined > 0 ? ((totalSalesCombined - totalDrugsUsed) / totalSalesCombined * 100).toFixed(1) : 0}%</div>
                    <div style={{ color: theme.textSecondary }}>• Expense Ratio: {totalSalesCombined > 0 ? (totalExpensesCombined / totalSalesCombined * 100).toFixed(1) : 0}%</div>
                    <div style={{ color: theme.textSecondary }}>• Items Cost Ratio: {totalSalesCombined > 0 ? (totalDrugsUsed / totalSalesCombined * 100).toFixed(1) : 0}%</div>
                    <div style={{ color: theme.textSecondary }}>• Purchase-to-Use Ratio: {totalDrugsUsed > 0 ? ((totalDrugsBought / totalDrugsUsed) * 100).toFixed(1) : 0}%</div>
                    <div style={{ color: theme.textSecondary }}>• Stock Change: {stockChange >= 0 ? 'Added' : 'Reduced'} {formatCurrency(Math.abs(stockChange))}</div>
                    <div style={{ color: theme.textSecondary }}>• Day Sales: {totalSalesCombined > 0 ? (totalDaySales / totalSalesCombined * 100).toFixed(1) : 0}%</div>
                    <div style={{ color: theme.textSecondary }}>• Night Sales: {totalSalesCombined > 0 ? (totalNightSales / totalSalesCombined * 100).toFixed(1) : 0}%</div>
                    <div style={{ color: theme.textSecondary }}>• Patients per Day (avg): {totalPatients > 0 ? (totalPatients / (dateFilterType === 'single' ? 1 : (dateFilterType === 'range' ? Math.ceil((new Date(selectedEndDate) - new Date(selectedDate)) / (1000 * 60 * 60 * 24)) + 1 : 1))).toFixed(1) : 0}</div>
                  </div>
                </div>
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}

export default SalesDetails;